import { useEffect, useRef, useState } from "react";
import "./HalloweenSplash.css";

// Gravedad en px/s² usada por los péndulos (hilo y nombre colgando).
const GRAVITY = 1800;
// Distancia del punto donde se ata el hilo (abdomen) a las patas que sujetan el nombre.
const CLAW_REACH = 42;
// Offset desde el borde superior del SVG hasta el punto de amarre del hilo.
const THREAD_ATTACH = 10;
// Largo efectivo del péndulo del nombre (desde las patas hasta su centro de masa).
const NAME_PENDULUM = 46;
const FADE_MS = 450;

function prefersReducedMotion() {
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

/**
 * Pantalla de carga de Halloween. La araña baja colgando de su hilo mientras
 * carga la tienda; cuando el branding está listo baja a medio camino, duda,
 * se deja caer sobre el nombre, lo atrapa (el hilo cede con el peso) y se lo
 * lleva hacia arriba con el nombre balanceándose.
 *
 * Todo el movimiento es una simulación simple (resortes + péndulos) en
 * requestAnimationFrame para que se sienta orgánico y no "de curva CSS".
 */
function HalloweenSplash({ ready, storeName, onDone }) {
  const [grabbing, setGrabbing] = useState(false);
  const [leaving, setLeaving] = useState(false);

  const spiderRef = useRef(null);
  const nameRef = useRef(null);
  const threadRef = useRef(null);

  const readyRef = useRef(ready);
  const onDoneRef = useRef(onDone);
  useEffect(() => {
    readyRef.current = ready;
  }, [ready]);
  useEffect(() => {
    onDoneRef.current = onDone;
  }, [onDone]);

  useEffect(() => {
    const reduced = prefersReducedMotion();
    let rafId = 0;
    let doneTimer = 0;
    let last = performance.now();

    const s = {
      phase: "enter",
      t: 0, // tiempo total
      pt: 0, // tiempo dentro de la fase actual
      y: -120, // borde superior de la araña
      vy: 0,
      phi: 0, // ángulo del hilo (péndulo largo)
      vphi: 0.06,
      th: 0, // ángulo del nombre colgando de las patas
      vth: 0,
      look: 0, // giro de "mirar" mientras duda
      hoverY: 0,
      attached: false,
      fading: false,
    };

    const go = (phase) => {
      s.phase = phase;
      s.pt = 0;
    };

    const finish = () => {
      setLeaving(true);
      doneTimer = window.setTimeout(() => onDoneRef.current?.(), FADE_MS);
    };

    const step = (now) => {
      const dt = Math.min(0.033, (now - last) / 1000);
      last = now;
      s.t += dt;
      s.pt += dt;

      const W = window.innerWidth;
      const H = window.innerHeight;
      const cx = W / 2;
      const nameTop = H * 0.5 - 28;
      const grabY = nameTop - THREAD_ATTACH - CLAW_REACH;
      const restY = H * 0.14;

      if (reduced) {
        if (readyRef.current) {
          finish();
          return;
        }
        rafId = requestAnimationFrame(step);
        return;
      }

      // Resorte hacia "target" (k = rigidez, c = amortiguación). null = sin resorte.
      let target = null;
      let k = 0;
      let c = 0;
      let ay = 0;
      s.look *= 0.9;

      switch (s.phase) {
        case "enter":
          // Cae desde arriba, rebota en el hilo y queda flotando suave.
          target = restY + 8 * Math.sin(s.t * 2.2);
          k = 70;
          c = 9;
          if (readyRef.current && s.t > 0.9) go("approach");
          break;

        case "approach":
          // Baja a medio camino, sin prisa.
          s.hoverY = restY + 0.55 * (grabY - restY);
          target = s.hoverY;
          k = 55;
          c = 10;
          if (s.pt > 0.75) go("hesitate");
          break;

        case "hesitate":
          // Retrocede un poco y mira a un lado y al otro.
          target = s.hoverY - 12;
          k = 60;
          c = 12;
          s.look = 0.24 * Math.sin((2 * Math.PI * s.pt) / 0.7);
          if (s.pt > 0.7) go("drop");
          break;

        case "drop":
          // Se deja caer rápido sobre el nombre.
          target = grabY;
          k = 200;
          c = 17;
          if (s.pt > 0.45) {
            go("grab");
            s.attached = true;
            setGrabbing(true);
            // El peso del nombre estira el hilo y lo pone a oscilar.
            s.vy += 260;
            s.vphi += 0.05;
            s.vth += 0.9;
          }
          break;

        case "grab":
          // Hilo elástico: rebota con el peso antes de estabilizarse.
          target = grabY;
          k = 80;
          c = 6;
          if (s.pt > 0.6) {
            go("lift");
            // Tirón inicial.
            s.vy = -60;
            s.vth += 1.1;
          }
          break;

        case "lift":
          // Sube cada vez más rápido.
          ay = -(1400 + 3000 * s.pt);
          if (s.pt > 0.55 && !s.fading) {
            s.fading = true;
            finish();
          }
          break;

        default:
          break;
      }

      if (target !== null) {
        s.vy += (k * (target - s.y) - c * s.vy) * dt;
      } else {
        s.vy += ay * dt;
      }
      s.y += s.vy * dt;

      // Péndulo del hilo, anclado arriba al centro de la pantalla.
      const threadLen = Math.max(40, s.y + THREAD_ATTACH + 10);
      s.vphi += (-(GRAVITY / threadLen) * s.phi - 0.35 * s.vphi) * dt;
      s.phi += s.vphi * dt;

      const ax = cx + threadLen * Math.sin(s.phi); // punto de amarre
      const ayAttach = s.y + THREAD_ATTACH;

      // Péndulo del nombre colgando de las patas; subir acelerando "pesa" más.
      if (s.attached) {
        const gEff = GRAVITY + Math.max(0, -ay);
        s.vth +=
          (-(gEff / NAME_PENDULUM) * Math.sin(s.th) - 2.2 * s.vth) * dt;
        s.th += s.vth * dt;
      }

      const spiderEl = spiderRef.current;
      if (spiderEl) {
        spiderEl.style.transform = `translate(${ax}px, ${s.y}px) translateX(-50%) rotate(${s.phi + s.look}rad)`;
      }

      const threadEl = threadRef.current;
      if (threadEl) {
        threadEl.setAttribute("x1", cx);
        threadEl.setAttribute("y1", -10);
        threadEl.setAttribute("x2", ax);
        threadEl.setAttribute("y2", ayAttach);
      }

      const nameEl = nameRef.current;
      if (nameEl) {
        if (s.attached) {
          const nx = ax - CLAW_REACH * Math.sin(s.phi);
          const ny = ayAttach + CLAW_REACH * Math.cos(s.phi);
          nameEl.style.transform = `translate(${nx}px, ${ny}px) translateX(-50%) rotate(${s.th}rad)`;
        } else {
          nameEl.style.transform = `translate(${cx}px, ${nameTop}px) translateX(-50%)`;
        }
      }

      // Cuando el nombre ya salió de pantalla no hace falta seguir simulando.
      if (s.phase === "lift" && s.y < -H) return;
      rafId = requestAnimationFrame(step);
    };

    rafId = requestAnimationFrame(step);
    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(doneTimer);
    };
  }, []);

  return (
    <div
      className={[
        "hw-splash",
        ready ? "hw-splash--ready" : "",
        grabbing ? "hw-splash--grabbing" : "",
        leaving ? "hw-splash--leaving" : "",
      ].join(" ")}
      role="status"
      aria-live="polite"
      aria-label="Cargando tienda"
    >
      <div className="hw-splash__moon" aria-hidden="true" />
      <svg
        className="hw-splash__web hw-splash__web--left"
        viewBox="0 0 120 120"
        aria-hidden="true"
      >
        <path d="M0 0 L120 0 M0 0 L0 120 M0 0 L110 60 M0 0 L60 110 M0 0 L95 95" />
        <path d="M30 0 Q24 10 28 16 Q14 14 0 30" />
        <path d="M62 0 Q50 22 55 32 Q30 28 22 52 Q12 48 0 62" />
        <path d="M95 0 Q78 34 84 48 Q50 46 44 84 Q24 78 0 95" />
      </svg>
      <svg
        className="hw-splash__web hw-splash__web--right"
        viewBox="0 0 120 120"
        aria-hidden="true"
      >
        <path d="M0 0 L120 0 M0 0 L0 120 M0 0 L110 60 M0 0 L60 110 M0 0 L95 95" />
        <path d="M30 0 Q24 10 28 16 Q14 14 0 30" />
        <path d="M62 0 Q50 22 55 32 Q30 28 22 52 Q12 48 0 62" />
        <path d="M95 0 Q78 34 84 48 Q50 46 44 84 Q24 78 0 95" />
      </svg>

      <svg className="hw-splash__thread" aria-hidden="true">
        <line ref={threadRef} x1="0" y1="0" x2="0" y2="0" />
      </svg>

      {storeName && (
        <div ref={nameRef} className="hw-splash__name">
          <span className="hw-splash__name-text">{storeName}</span>
        </div>
      )}

      <div ref={spiderRef} className="hw-spider" aria-hidden="true">
        <svg className="hw-spider__svg" viewBox="0 0 100 90">
          <g className="hw-spider__legs">
            <path d="M42 30 Q24 12 10 24" />
            <path d="M41 37 Q18 30 6 44" />
            <path d="M41 44 Q20 50 12 64" />
            <path d="M58 30 Q76 12 90 24" />
            <path d="M59 37 Q82 30 94 44" />
            <path d="M59 44 Q80 50 88 64" />
          </g>
          <path
            className="hw-spider__claw hw-spider__claw--l"
            d="M44 54 Q28 66 32 86"
          />
          <path
            className="hw-spider__claw hw-spider__claw--r"
            d="M56 54 Q72 66 68 86"
          />
          <ellipse className="hw-spider__abdomen" cx="50" cy="32" rx="15" ry="18" />
          <path className="hw-spider__mark" d="M50 22 L54 30 L50 38 L46 30 Z" />
          <circle className="hw-spider__head" cx="50" cy="56" r="10" />
          <circle className="hw-spider__eye" cx="46" cy="58" r="2.4" />
          <circle className="hw-spider__eye" cx="54" cy="58" r="2.4" />
        </svg>
      </div>

      <p className="hw-splash__hint">Cargando tienda…</p>
    </div>
  );
}

export default HalloweenSplash;
